import { useState, useCallback, useEffect } from 'react';
import { useBrandSettings } from '../hooks/useBrandSettings';
import { useHolderSwitcher } from '@/components/HolderSwitcher';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/use-toast';
import { ChevronDown, Plus, Check, Copy, Clock } from 'lucide-react';
import { cn } from '@/lib/utils';
import { MediaPickerModal, type MediaPickerFile } from '../components/MediaPickerModal';
import { api } from '@/lib/api';
import { GeneratedImageResult } from '../hooks';
import { FloppyIcon } from '../components/Icons';

// ---------------------------------------------------------------------------
// Helper primitives
// ---------------------------------------------------------------------------

function SectionTitle({
  title,
  subtitle,
  badge,
}: {
  title: string;
  subtitle?: string;
  badge?: React.ReactNode;
}) {
  return (
    <div className="mb-3">
      <div className="flex items-center gap-2">
        <h2 className="font-satoshi text-xl font-normal text-foreground">{title}</h2>
        {badge}
      </div>
      {subtitle && (
        <p className="font-satoshi text-[13px] text-muted-foreground mt-0.5">{subtitle}</p>
      )}
    </div>
  );
}

function CardLabel({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <p className={cn('font-mono text-[12px] font-medium text-foreground/80 mb-1.5', className)}>
      {children}
    </p>
  );
}

function Hint({ children }: { children: React.ReactNode }) {
  return <p className="font-mono  text-[11px] text-muted-foreground mt-1">{children}</p>;
}

function AiContextBadge() {
  return (
    <span className="hidden font-mono lg:inline-flex items-center gap-1 rounded-full bg-[#EEEDFE] dark:bg-[#2a2860] text-[#3C3489] dark:text-[#8b85e8] text-[10px] font-medium px-2.5 py-0.5">
      <Clock className="h-3 w-3" />
      AI context
    </span>
  );
}

function OptionalBadge() {
  return (
    <span className="font-mono inline-flex items-center rounded-full bg-muted text-muted-foreground text-[10px] font-medium px-2.5 py-0.5">
      optional
    </span>
  );
}

function RequiredBadge() {
  return (
    <span className="font-mono inline-flex items-center rounded-full bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 text-[10px] font-medium px-2.5 py-0.5">
      required
    </span>
  );
}

interface InputFieldProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  hint?: string;
  badge?: React.ReactNode;
}

function InputField({ label, hint, badge, className, ...props }: InputFieldProps) {
  return (
    <div>
      {label && (
        <div className="flex items-center gap-1.5 mb-1.5">
          <CardLabel className="mb-0">{label}</CardLabel>
          {badge}
        </div>
      )}
      <input
        className={cn(
          'w-full h-[38px] rounded-[8px] px-[11px] py-2 bg-background text-sm text-foreground',
          'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-0',
          'resize-none rounded-xl border border-border bg-background px-3 py-2 text-[12px] text-foreground placeholder:text-muted-foreground/50 outline-none focus:border-primary leading-relaxed rounded-[10px] outline-none transition-all duration-150 shadow-social-card',
          className,
        )}
        {...props}
      />
      {hint && <Hint>{hint}</Hint>}
    </div>
  );
}

interface TextareaFieldProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  hint?: string;
  badge?: React.ReactNode;
  maxCount?: number;
}

function TextareaField({
  label,
  hint,
  badge,
  maxCount,
  className,
  value,
  ...props
}: TextareaFieldProps) {
  const charCount = typeof value === 'string' ? value.length : 0;
  return (
    <div>
      {label && (
        <div className="flex items-center gap-1.5 mb-1.5">
          <CardLabel className="mb-0">{label}</CardLabel>
          {badge}
        </div>
      )}
      <div className="relative">
        <textarea
          className={cn(
            'w-full rounded-[8px] px-[11px] py-2 border border-input bg-background text-sm text-foreground resize-none',
            'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-0',
            'resize-none rounded-xl border border-border bg-background px-3 py-2 text-[12px] text-foreground placeholder:text-muted-foreground/50 outline-none focus:border-primary leading-relaxed rounded-[10px] outline-none transition-all duration-150 shadow-social-card',
            className,
          )}
          value={value}
          {...props}
        />
        {maxCount && (
          <span className="absolute bottom-2 right-2 text-[10px] text-muted-foreground">
            {charCount} / {maxCount}
          </span>
        )}
      </div>
      {hint && <Hint>{hint}</Hint>}
    </div>
  );
}

interface SelectFieldProps {
  label?: string;
  hint?: string;
  badge?: React.ReactNode;
  value: string;
  onChange: (v: string) => void;
  options: Array<{ value: string; label: string }>;
}

function SelectField({ label, hint, badge, value, onChange, options }: SelectFieldProps) {
  return (
    <div>
      {label && (
        <div className="flex items-center gap-1.5 mb-1.5">
          <CardLabel className="mb-0">{label}</CardLabel>
          {badge}
        </div>
      )}
      <div className="relative">
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={cn(
            'w-full h-[38px] rounded-[8px] px-[11px] pr-9 border border-input bg-background text-sm text-foreground appearance-none',
            'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-0',
            'resize-none rounded-xl border border-border bg-background px-3 py-2 text-[12px] text-foreground placeholder:text-muted-foreground/50 outline-none focus:border-primary leading-relaxed rounded-[10px] outline-none transition-all duration-150 shadow-social-card',
          )}
        >
          {options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
      </div>
      {hint && <Hint>{hint}</Hint>}
    </div>
  );
}

interface ToggleRowProps {
  label: string;
  sublabel: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}

function ToggleRow({ label, sublabel, checked, onChange }: ToggleRowProps) {
  return (
    <div className="flex items-center justify-between py-3">
      <div>
        <p className="text-[13px] font-medium text-foreground">{label}</p>
        <p className="text-[11px] text-muted-foreground mt-0.5">{sublabel}</p>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className={cn(
          'relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent',
          'transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
          checked ? 'bg-foreground dark:bg-foreground' : 'bg-muted-foreground/30',
        )}
      >
        <span
          className={cn(
            'pointer-events-none inline-block h-4 w-4 rounded-full bg-background shadow-lg ring-0',
            'transition-transform duration-200 ease-in-out',
            checked ? 'translate-x-4' : 'translate-x-0',
          )}
        />
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section 1 — Identity
// ---------------------------------------------------------------------------

const INDUSTRY_OPTIONS = [
  { value: '', label: 'Select industry' },
  { value: 'consumer_tech', label: 'Consumer tech' },
  { value: 'b2b_saas', label: 'B2B SaaS' },
  { value: 'ecommerce', label: 'E-commerce' },
  { value: 'media', label: 'Media & Publishing' },
  { value: 'finance', label: 'Finance & Fintech' },
  { value: 'health', label: 'Health & Wellness' },
  { value: 'education', label: 'Education' },
  { value: 'food_beverage', label: 'Food & Beverage' },
  { value: 'fashion', label: 'Fashion & Lifestyle' },
  { value: 'travel', label: 'Travel & Hospitality' },
  { value: 'nonprofit', label: 'Non-profit' },
  { value: 'agency', label: 'Agency & Services' },
  { value: 'other', label: 'Other' },
];

export interface IdentityState {
  brandName: string;
  industry: string;
  tagline: string;
  website: string;
}

function IdentitySection({
  identity,
  onChange,
}: {
  identity: IdentityState;
  onChange: (patch: Partial<IdentityState>) => void;
}) {
  return (
    <section>
      <SectionTitle title="Identity" subtitle="Core information about your brand" />
      <div className="border border-border rounded-xl bg-background p-5 space-y-4 shadow-social-card">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <div className="flex items-center gap-1.5 mb-1.5">
              <CardLabel className="mb-0">Brand name</CardLabel>
              <RequiredBadge />
            </div>
            <input
              className={cn(
                'w-full h-[38px] rounded-[8px] px-[11px] py-2 border border-input bg-background text-sm text-foreground',
                'focus:outline-none focus:ring-2 focus:ring-ring',
                'resize-none rounded-xl border border-border bg-background px-3 py-2 text-[12px] text-foreground placeholder:text-muted-foreground/50 outline-none focus:border-primary leading-relaxed rounded-[10px] outline-none transition-all duration-150 shadow-social-card',
              )}
              value={identity.brandName}
              onChange={(e) => onChange({ brandName: e.target.value })}
              placeholder="e.g. Acme Co."
            />
          </div>
          <SelectField
            label="Industry"
            value={identity.industry}
            onChange={(v) => onChange({ industry: v })}
            options={INDUSTRY_OPTIONS}
          />
        </div>
        <InputField
          label="Brand tagline"
          value={identity.tagline}
          onChange={(e) => onChange({ tagline: e.target.value })}
          placeholder="e.g. Built for the bold"
          hint="Used as a short signature when the AI writes captions"
        />
        <InputField
          label="Website"
          type="url"
          value={identity.website}
          onChange={(e) => onChange({ website: e.target.value })}
          placeholder="https://yourbrand.com"
        />
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Section 2 — Logo
// ---------------------------------------------------------------------------

const LOGO_USAGE_OPTIONS = [
  { value: 'always', label: 'Always include in templates' },
  { value: 'sometimes', label: 'Include when space allows' },
  { value: 'never', label: 'Never include' },
];

const LOGO_PLACEMENT_OPTIONS = [
  { value: 'bottom_right', label: 'Bottom right' },
  { value: 'bottom_left', label: 'Bottom left' },
  { value: 'top_right', label: 'Top right' },
  { value: 'top_left', label: 'Top left' },
  { value: 'center', label: 'Center' },
];

interface LogoState {
  lightLogoFileId: string | null;
  darkLogoFileId: string | null;
  usage: string;
  placement: string;
}

function LogoSection({
  logo,
  initials,
  onChange,
}: {
  logo: LogoState;
  initials: string;
  onChange: (patch: Partial<LogoState>) => void;
}) {
  const [pickerTarget, setPickerTarget] = useState<'light' | 'dark' | null>(null);
  const [lightPreviewUrl, setLightPreviewUrl] = useState<string | null>(null);
  const [darkPreviewUrl, setDarkPreviewUrl] = useState<string | null>(null);

  // Fetch presigned preview URLs for saved logo fileIds
  useEffect(() => {
    if (logo.lightLogoFileId && !lightPreviewUrl) {
      api
        .getDownloadUrl(logo.lightLogoFileId)
        .then(({ downloadUrl }) => setLightPreviewUrl(downloadUrl))
        .catch(() => undefined);
    }
  }, [logo.lightLogoFileId, lightPreviewUrl]);

  useEffect(() => {
    if (logo.darkLogoFileId && !darkPreviewUrl) {
      api
        .getDownloadUrl(logo.darkLogoFileId)
        .then(({ downloadUrl }) => setDarkPreviewUrl(downloadUrl))
        .catch(() => undefined);
    }
  }, [logo.darkLogoFileId, darkPreviewUrl]);

  const handleFilesSelected = useCallback(
    (files: MediaPickerFile[]) => {
      const file = files[0];
      if (!file || !pickerTarget) return;
      if (pickerTarget === 'light') {
        onChange({ lightLogoFileId: file.fileId });
        setLightPreviewUrl(file.downloadUrl ?? null);
      } else {
        onChange({ darkLogoFileId: file.fileId });
        setDarkPreviewUrl(file.downloadUrl ?? null);
      }
      setPickerTarget(null);
    },
    [pickerTarget, onChange],
  );

  const handleImageGenerated = useCallback(
    (result: GeneratedImageResult) => {
      if (!result.fileId || !pickerTarget) return;
      if (pickerTarget === 'light') {
        onChange({ lightLogoFileId: result.fileId });
        setLightPreviewUrl(result.imageUrl ?? null);
      } else {
        onChange({ darkLogoFileId: result.fileId });
        setDarkPreviewUrl(result.imageUrl ?? null);
      }
      setPickerTarget(null);
    },
    [pickerTarget, onChange],
  );

  return (
    <section>
      <SectionTitle
        title="Logo"
        subtitle="Upload light and dark versions — both may be used on different post templates"
      />
      <div className="border border-border rounded-xl bg-background p-5 space-y-4 shadow-social-card">
        <div className="flex items-start gap-4">
          <div className="flex gap-3">
            {/* Light bg thumbnail */}
            <div className="flex flex-col items-center gap-1">
              <p className="text-[11px] text-muted-foreground">Light bg</p>
              <button
                type="button"
                onClick={() => setPickerTarget('light')}
                className="w-[72px] h-[72px] rounded-[10px] bg-white border border-border flex items-center justify-center overflow-hidden hover:border-foreground/40 transition-colors"
                title="Pick light logo"
              >
                {lightPreviewUrl ? (
                  <img
                    src={lightPreviewUrl}
                    alt="Light logo"
                    className="w-full h-full object-contain p-1"
                  />
                ) : logo.lightLogoFileId ? (
                  <span className="text-[10px] text-center text-muted-foreground px-1 leading-tight">
                    Logo saved
                  </span>
                ) : (
                  <span
                    className="text-xl text-muted-foreground"
                    style={{ fontFamily: "'DM Serif Display', serif" }}
                  >
                    {initials || 'Br'}
                  </span>
                )}
              </button>
            </div>
            {/* Dark bg thumbnail */}
            <div className="flex flex-col items-center gap-1">
              <p className="text-[11px] text-muted-foreground">Dark bg</p>
              <button
                type="button"
                onClick={() => setPickerTarget('dark')}
                className="w-[72px] h-[72px] rounded-[10px] flex items-center justify-center overflow-hidden hover:opacity-80 transition-opacity"
                style={{ background: 'var(--social-code-bg)' }}
                title="Pick dark logo"
              >
                {darkPreviewUrl ? (
                  <img
                    src={darkPreviewUrl}
                    alt="Dark logo"
                    className="w-full h-full object-contain p-1"
                  />
                ) : logo.darkLogoFileId ? (
                  <span
                    className="text-[10px] text-center px-1 leading-tight"
                    style={{ color: 'rgba(255,255,255,0.6)' }}
                  >
                    Logo saved
                  </span>
                ) : (
                  <span
                    className="text-xl"
                    style={{
                      color: 'rgba(255,255,255,0.4)',
                      fontFamily: "'DM Serif Display', serif",
                    }}
                  >
                    {initials || 'Br'}
                  </span>
                )}
              </button>
            </div>
          </div>
          <div className="flex-1">
            <p className="text-[13px] font-semibold text-foreground">
              {logo.lightLogoFileId || logo.darkLogoFileId ? 'Logo uploaded' : 'No logo uploaded'}
            </p>
            <p className="text-[11px] text-muted-foreground mt-0.5">
              SVG, PNG or WebP — 512×512px minimum, transparent background recommended
            </p>
            <div className="flex flex-wrap gap-2 mt-3">
              <Button
                variant="outline"
                size="sm"
                className="text-xs h-8"
                onClick={() => setPickerTarget('light')}
              >
                ↑ Upload light logo
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="text-xs h-8"
                onClick={() => setPickerTarget('dark')}
              >
                ↑ Upload dark logo
              </Button>
            </div>
          </div>
        </div>
        <div className="pt-4 border-t border-border grid grid-cols-1 sm:grid-cols-2 gap-4">
          <SelectField
            label="Logo usage in posts"
            value={logo.usage}
            onChange={(v) => onChange({ usage: v })}
            options={LOGO_USAGE_OPTIONS}
          />
          <SelectField
            label="Logo placement"
            value={logo.placement}
            onChange={(v) => onChange({ placement: v })}
            options={LOGO_PLACEMENT_OPTIONS}
          />
        </div>
      </div>

      <MediaPickerModal
        open={pickerTarget !== null}
        onOpenChange={(open) => {
          if (!open) setPickerTarget(null);
        }}
        onFilesSelected={handleFilesSelected}
        onImageGenerated={handleImageGenerated}
        acceptedMimeTypes="image/png,image/jpeg,image/webp,image/svg+xml"
        remainingSlots={1}
      />
    </section>
  );
}

// ---------------------------------------------------------------------------
// Section 3 — Color Palette
// ---------------------------------------------------------------------------

export interface ColorSwatch {
  name: string;
  hex: string;
}

function ColorPaletteSection({
  palette,
  onChange,
}: {
  palette: ColorSwatch[];
  onChange: (palette: ColorSwatch[]) => void;
}) {
  const [addingColor, setAddingColor] = useState(false);
  const [newColor, setNewColor] = useState({ name: '', hex: '#000000' });

  function addSwatch() {
    if (!newColor.name.trim() || !newColor.hex) return;
    onChange([...palette, { name: newColor.name.trim(), hex: newColor.hex }]);
    setNewColor({ name: '', hex: '#000000' });
    setAddingColor(false);
  }

  function removeSwatch(index: number) {
    onChange(palette.filter((_, i) => i !== index));
  }

  return (
    <section>
      <SectionTitle title="Color palette" subtitle="Define your brand's visual identity" />
      <div className="rounded-xl bg-background p-5 space-y-4 shadow-social-card">
        <div
          className="grid gap-[10px]"
          style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))' }}
        >
          {palette.map((swatch, idx) => (
            <div
              key={idx}
              className="border border-border rounded-[9px] overflow-hidden cursor-pointer group relative"
              onClick={() => removeSwatch(idx)}
              title="Click to remove"
            >
              <div
                className="w-full h-[52px]"
                style={{
                  background: swatch.hex,
                  borderBottom: `1px solid rgba(0,0,0,0.08)`,
                }}
              />
              <div className="bg-card px-[10px] py-2">
                <p className="text-[11px] font-medium text-foreground/80 truncate">{swatch.name}</p>
                <p className="text-[10px] font-mono text-muted-foreground">{swatch.hex}</p>
              </div>
              <div className="absolute inset-0 bg-black/5 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                <span className="text-[11px] text-white bg-black/60 px-2 py-0.5 rounded">
                  Remove
                </span>
              </div>
            </div>
          ))}

          {addingColor ? (
            <div className="border border-border rounded-[9px] overflow-hidden p-3 space-y-2 bg-card">
              <input
                type="color"
                value={newColor.hex}
                onChange={(e) => setNewColor((p) => ({ ...p, hex: e.target.value }))}
                className={cn(
                  'w-full h-8 cursor-pointer',
                  'resize-none p-0 rounded-[10px] outline-none',
                )}
              />
              <input
                placeholder="Name (e.g. Primary)"
                value={newColor.name}
                onChange={(e) => setNewColor((p) => ({ ...p, name: e.target.value }))}
                className={cn(
                  'w-full text-[12px] border border-input rounded px-2 py-1 bg-background',
                  'resize-none rounded-xl border border-border bg-background px-3 py-2 text-[12px] text-foreground placeholder:text-muted-foreground/50 outline-none focus:border-primary leading-relaxed rounded-[10px] outline-none transition-all duration-150 shadow-social-card',
                )}
              />
              <div className="flex gap-1">
                <Button size="sm" className="flex-1 h-7 text-[10px] font-mono" onClick={addSwatch}>
                  Add
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 text-[11px]"
                  onClick={() => setAddingColor(false)}
                >
                  ✕
                </Button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setAddingColor(true)}
              className={cn(
                'border-[1.5px] border-dashed border-border rounded-[9px] flex flex-col items-center justify-center gap-1.5',
                'h-[92px] text-muted-foreground hover:border-foreground/40 hover:text-foreground/60 transition-colors',
              )}
            >
              <Plus className="h-[18px] w-[18px]" />
              <span className="text-[12px]">Add color</span>
            </button>
          )}
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Section 4 — Brand Voice
// ---------------------------------------------------------------------------

const PERSONAS = [
  {
    key: 'expert',
    name: 'The Expert',
    description:
      'Confident, knowledgeable, authoritative. Builds trust through depth and precision.',
  },
  {
    key: 'friend',
    name: 'The Friend',
    description: 'Warm, casual, relatable. Talks like a trusted peer, not a brand.',
  },
  {
    key: 'disruptor',
    name: 'The Disruptor',
    description: 'Bold, provocative, counter-narrative. Challenges the status quo.',
  },
  {
    key: 'storyteller',
    name: 'The Storyteller',
    description: 'Narrative-driven, evocative, human. Leads with emotion and imagery.',
  },
];

const TONE_ROWS = [
  { key: 'formality', label: 'Formality', left: 'Casual', right: 'Formal' },
  { key: 'humor', label: 'Humor', left: 'Serious', right: 'Playful' },
  { key: 'boldness', label: 'Boldness', left: 'Understated', right: 'Bold' },
  { key: 'empathy', label: 'Empathy', left: 'Direct', right: 'Empathetic' },
] as const;

const POSITIVE_DESCRIPTORS = [
  'Authentic',
  'Direct',
  'Witty',
  'Conversational',
  'Confident',
  'Inspiring',
  'Educational',
  'Sophisticated',
  'Energetic',
  'Warm',
  'Minimalist',
  'Nostalgic',
  'Aspirational',
  'Rebellious',
];

const AVOID_DESCRIPTORS = [
  'Corporate jargon',
  'Clickbait',
  'Excessive exclamation marks',
  'Passive voice',
  'Hashtag stuffing',
  'Overused buzzwords',
  'All caps',
  'Vague CTAs',
];

const SIGN_OFF_OPTIONS = [
  { value: 'none', label: 'No sign-off' },
  { value: 'subtle_tagline', label: 'Subtle tagline reference' },
  { value: 'brand_mention', label: 'Brand name mention' },
  { value: 'call_to_action', label: 'Direct call to action' },
  { value: 'question', label: 'Closing question' },
];

const CTA_OPTIONS = [
  { value: 'none', label: 'No CTA' },
  { value: 'soft_engagement', label: 'Soft engagement (reply, save)' },
  { value: 'follow', label: 'Follow prompt' },
  { value: 'link_click', label: 'Link click' },
  { value: 'sale', label: 'Direct sale' },
  { value: 'signup', label: 'Sign-up / lead gen' },
];

const EMOJI_OPTIONS = [
  { value: 'none', label: 'No emoji' },
  { value: 'minimal', label: 'Minimal (1–2 per post)' },
  { value: 'moderate', label: 'Moderate (3–5 per post)' },
  { value: 'frequent', label: 'Frequent' },
];

const LENGTH_OPTIONS = [
  { value: 'short', label: 'Short (1-2 lines)' },
  { value: 'medium', label: 'Medium (3-5 lines)' },
  { value: 'long', label: 'Long (6+ lines)' },
  { value: 'varies', label: 'Varies by platform' },
];

export interface VoiceState {
  personaArchetype: string;
  toneSpectrum: { formality: number; humor: number; boldness: number; empathy: number };
  voiceDescriptors: string[];
  avoidDescriptors: string[];
  brandDescription: string;
  targetAudience: string;
  contentPillars: string;
  pillarWeights: Record<string, number>;
  postStructure: string;
  differentiationFraming: string;
  personaVoice: string;
  signOffStyle: string;
  defaultCTAType: string;
  wordsToUse: string;
  wordsToNeverUse: string;
  brandSpecificTerms: string;
  hashtagStrategy: string;
  emojiUsage: string;
  postLengthPreference: string;
  useLineBreaks: boolean;
  includeQuestions: boolean;
  referenceBrandColor: boolean;
}

// ---------------------------------------------------------------------------
// Pillar weights sub-component
// ---------------------------------------------------------------------------

function PillarWeightsEditor({
  pillarsRaw,
  weights,
  onChange,
}: {
  pillarsRaw: string;
  weights: Record<string, number>;
  onChange: (weights: Record<string, number>) => void;
}) {
  const pillars = pillarsRaw
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);

  function setWeight(pillar: string, value: number) {
    onChange({ ...weights, [pillar]: Math.min(100, Math.max(0, value)) });
  }

  const total = pillars.reduce((sum, p) => sum + (weights[p] ?? 0), 0);

  return (
    <div className="pt-4 border-t border-border space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <CardLabel className="mb-0">Pillar weights</CardLabel>
          <OptionalBadge />
        </div>
        <span
          className={cn(
            'text-[11px] !font-mono',
            total > 100
              ? 'text-destructive'
              : total === 100
                ? 'text-[#085041]'
                : 'text-muted-foreground',
          )}
        >
          {total}% / 100%
        </span>
      </div>
      <Hint>
        Set how often the AI should draw from each pillar. Leave at 0 to distribute evenly.
      </Hint>
      <div className="space-y-2 mt-2">
        {pillars.map((pillar) => (
          <div key={pillar} className="flex items-center gap-3">
            <span className="text-[12px] text-foreground/80 flex-1 truncate">{pillar}</span>
            <div className="flex items-center gap-1.5 shrink-0">
              <input
                type="range"
                min={0}
                max={100}
                step={5}
                value={weights[pillar] ?? 0}
                onChange={(e) => setWeight(pillar, Number(e.target.value))}
                className={cn('w-24 h-1 accent-foreground cursor-pointer')}
              />
              <span className="text-[12px] text-muted-foreground w-8 text-right !font-mono">
                {weights[pillar] ?? 0}%
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function BrandVoiceSection({
  voice,
  palette,
  identity,
  platformOverrides,
  onChange,
  onOverridesChange,
}: {
  voice: VoiceState;
  palette: ColorSwatch[];
  identity: IdentityState;
  platformOverrides: PlatformOverride[];
  onChange: (patch: Partial<VoiceState>) => void;
  onOverridesChange: (overrides: PlatformOverride[]) => void;
}) {
  function toggleDescriptor(
    list: string[],
    item: string,
    key: 'voiceDescriptors' | 'avoidDescriptors',
  ) {
    const next = list.includes(item) ? list.filter((d) => d !== item) : [...list, item];
    onChange({ [key]: next });
  }

  function updateTone(key: keyof VoiceState['toneSpectrum'], value: number) {
    onChange({ toneSpectrum: { ...voice.toneSpectrum, [key]: value } });
  }

  return (
    <section>
      <SectionTitle
        title="Brand voice"
        subtitle="Everything below is injected into the AI's context when generating posts. Be precise — this is your brand's DNA."
        badge={<AiContextBadge />}
      />
      <div className="space-y-3.5">
        {/* Sub-card A — Persona archetype */}
        <div className="rounded-xl bg-background p-5 space-y-4 shadow-social-card">
          <CardLabel>Brand persona archetype</CardLabel>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
            {PERSONAS.map((p) => {
              const active = voice.personaArchetype === p.key;
              return (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => onChange({ personaArchetype: p.key })}
                  className={cn(
                    'relative text-left rounded-[9px] p-[14px_16px] border transition-colors',
                    active
                      ? 'bg-card border-foreground'
                      : 'bg-muted/50 border-border hover:border-foreground/40',
                  )}
                >
                  <div
                    className={cn(
                      'absolute top-3 right-3 h-[18px] w-[18px] rounded-full border-[1.5px] flex items-center justify-center',
                      active ? 'bg-foreground border-foreground' : 'bg-background border-border',
                    )}
                  >
                    {active && <Check className="h-2.5 w-2.5 text-background" strokeWidth={3} />}
                  </div>
                  <p className="text-[13px] font-medium text-foreground mb-1">{p.name}</p>
                  <p className="text-[11px] text-muted-foreground leading-relaxed pr-6">
                    {p.description}
                  </p>
                </button>
              );
            })}
          </div>
        </div>

        {/* Sub-card B — Tone spectrum */}
        <div className="rounded-xl bg-background p-5 space-y-4 shadow-social-card">
          <CardLabel className="mb-3">Tone spectrum</CardLabel>
          <div className="space-y-0">
            {TONE_ROWS.map((row, idx) => (
              <div
                key={row.key}
                className={cn('py-2.5', idx < TONE_ROWS.length - 1 && 'border-b border-border')}
              >
                <div className="flex items-center gap-3">
                  <span className="text-[12px] font-medium text-foreground/80 min-w-[90px]">
                    {row.label}
                  </span>
                  <input
                    type="range"
                    min={0}
                    max={100}
                    value={voice.toneSpectrum[row.key]}
                    onChange={(e) => updateTone(row.key, Number(e.target.value))}
                    className={cn('flex-1 h-1 cursor-pointer bg-[#0360FE]/70')}
                  />
                </div>
                <div className="flex justify-between ml-[93px] sm:ml-[102px] mt-1">
                  <span className="text-[10px] text-muted-foreground !font-mono">{row.left}</span>
                  <span className="text-[10px] text-muted-foreground !font-mono">{row.right}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Sub-card C — Voice descriptors */}
        <div className="rounded-xl bg-background p-5 space-y-4 shadow-social-card">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 mb-3">
            <CardLabel className="mb-0">Voice descriptors</CardLabel>
            <span className="text-[11px] text-muted-foreground">
              Click to toggle — selected are passed to AI
            </span>
          </div>
          <div>
            <p className="text-[11px] text-muted-foreground mb-2">Positive traits</p>
            <div className="flex flex-wrap gap-2">
              {POSITIVE_DESCRIPTORS.map((d) => {
                const active = voice.voiceDescriptors.includes(d);
                return (
                  <button
                    key={d}
                    type="button"
                    onClick={() => toggleDescriptor(voice.voiceDescriptors, d, 'voiceDescriptors')}
                    className={cn(
                      'rounded-full text-[12px] px-3 py-1 border transition-colors',
                      active
                        ? 'bg-foreground text-background border-foreground'
                        : 'bg-background text-foreground/80 border-border hover:border-foreground/40',
                    )}
                  >
                    {d}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="border-t border-border my-4" />
          <div>
            <p className="text-[11px] text-muted-foreground mb-2">
              Avoid (the AI will steer away from these)
            </p>
            <div className="flex flex-wrap gap-2">
              {AVOID_DESCRIPTORS.map((d) => {
                const active = voice.avoidDescriptors.includes(d);
                return (
                  <button
                    key={d}
                    type="button"
                    onClick={() => toggleDescriptor(voice.avoidDescriptors, d, 'avoidDescriptors')}
                    className={cn(
                      'rounded-full text-[12px] px-3 py-1 border transition-colors',
                      active
                        ? 'bg-[#E1F5EE] dark:bg-[#0d3d2d] text-[#085041] dark:text-[#5ecba1] border-transparent'
                        : 'bg-background text-foreground/80 border-border hover:border-foreground/40',
                    )}
                  >
                    {d}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Sub-card D — Brand description, audience, pillars */}
        <div className="rounded-xl bg-background p-5 space-y-4 shadow-social-card">
          <TextareaField
            label="Brand description"
            value={voice.brandDescription}
            onChange={(e) => onChange({ brandDescription: e.target.value })}
            maxCount={300}
            rows={4}
            hint="Tip: write this as if briefing a copywriter — not a mission statement"
            placeholder="Describe your brand as if briefing a copywriter..."
          />
          <TextareaField
            label="Target audience"
            value={voice.targetAudience}
            onChange={(e) => onChange({ targetAudience: e.target.value })}
            maxCount={200}
            rows={3}
            placeholder="Who is your ideal customer or follower?"
          />
          <TextareaField
            label="Persona voice"
            value={voice.personaVoice}
            onChange={(e) => onChange({ personaVoice: e.target.value })}
            maxCount={300}
            rows={4}
            hint="Tip: describe the brand's (brand, influencer, person) voice/tone/personality"
            placeholder="Describe the voice, tone, and personality of this brand..."
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <SelectField
              label="Signature sign-off style"
              value={voice.signOffStyle}
              onChange={(v) => onChange({ signOffStyle: v })}
              options={SIGN_OFF_OPTIONS}
              hint="How posts should end"
            />
            <SelectField
              label="Default CTA type"
              value={voice.defaultCTAType}
              onChange={(v) => onChange({ defaultCTAType: v })}
              options={CTA_OPTIONS}
            />
          </div>

          <InputField
            badge={<OptionalBadge />}
            label="Content pillars"
            value={voice.contentPillars}
            onChange={(e) => onChange({ contentPillars: e.target.value })}
            hint="Comma-separated themes the AI should draw from when generating post ideas"
            placeholder="Product updates, Behind the scenes, Tips & tricks"
          />

          {/* Pillar weights — shown when pillars are defined */}
          {voice.contentPillars.trim() && (
            <PillarWeightsEditor
              pillarsRaw={voice.contentPillars}
              weights={voice.pillarWeights}
              onChange={(w) => onChange({ pillarWeights: w })}
            />
          )}
        </div>

        {/* Sub-card D2 — Post structure & differentiation */}
        <div className="rounded-xl bg-background p-5 space-y-4 shadow-social-card">
          <div>
            <div className="flex items-center gap-1.5 mb-1.5">
              <CardLabel className="mb-0">Post structure</CardLabel>
              <OptionalBadge />
            </div>
            <textarea
              value={voice.postStructure}
              onChange={(e) => onChange({ postStructure: e.target.value })}
              rows={3}
              placeholder="Hook → Context → Insight → CTA. Short opener to grab attention, one insight or reveal, punchy close with a nudge."
              className={cn(
                'w-full rounded-[8px] px-[11px] py-2 border border-input bg-background text-sm text-foreground resize-none',
                'focus:outline-none focus:ring-2 focus:ring-ring',
                'resize-none rounded-xl border border-border bg-background px-3 py-2 text-[12px] text-foreground placeholder:text-muted-foreground/50 outline-none focus:border-primary leading-relaxed rounded-[10px] outline-none transition-all duration-150 shadow-social-card',
              )}
            />
            <Hint>
              Name the skeleton your posts follow so the AI scaffolds correctly without guessing.
            </Hint>
          </div>
          <div>
            <div className="flex items-center gap-1.5 mb-1.5">
              <CardLabel className="mb-0">Competitive differentiation framing</CardLabel>
              <OptionalBadge />
            </div>
            <input
              value={voice.differentiationFraming}
              onChange={(e) => onChange({ differentiationFraming: e.target.value })}
              placeholder="e.g. we're infra, not apps — not another chatbot wrapper"
              className={cn(
                'w-full h-[38px] rounded-[8px] px-[11px] py-2 border border-input bg-background text-sm text-foreground',
                'focus:outline-none focus:ring-2 focus:ring-ring',
                'resize-none rounded-xl border border-border bg-background px-3 py-2 text-[12px] text-foreground placeholder:text-muted-foreground/50 outline-none focus:border-primary leading-relaxed rounded-[10px] outline-none transition-all duration-150 shadow-social-card',
              )}
            />
            <Hint>
              One sentence that anchors how you talk about competitors and positioning without
              naming them.
            </Hint>
          </div>
        </div>

        {/* Sub-card E — Vocabulary & formatting */}
        <div className="rounded-xl bg-background p-5 space-y-4 shadow-social-card">
          <CardLabel>Vocabulary & formatting</CardLabel>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <TextareaField
              label="Words we use"
              badge={<OptionalBadge />}
              value={voice.wordsToUse}
              onChange={(e) => onChange({ wordsToUse: e.target.value })}
              rows={3}
              hint="Comma-separated. AI will weave these naturally into copy."
              placeholder="ship, craft, build, create..."
            />
            <TextareaField
              label="Words we never use"
              value={voice.wordsToNeverUse}
              onChange={(e) => onChange({ wordsToNeverUse: e.target.value })}
              rows={3}
              hint="Hard blocks — AI will never use these."
              placeholder="synergy, leverage, disrupt..."
            />
            <TextareaField
              label="Brand-specific terms / spellings"
              value={voice.brandSpecificTerms}
              onChange={(e) => onChange({ brandSpecificTerms: e.target.value })}
              rows={3}
              hint="Spelling or capitalization rules the AI must respect"
              placeholder="AcmePro (not 'Acme Pro')..."
            />
            <TextareaField
              label="Hashtag strategy"
              value={voice.hashtagStrategy}
              onChange={(e) => onChange({ hashtagStrategy: e.target.value })}
              rows={3}
              hint="Per-post hashtag count, must-use tags, platform-specific rules"
              placeholder="3–5 per post max..."
            />
          </div>
        </div>

        {/* Emoji & formatting card */}
        <div className="rounded-xl bg-background p-5 space-y-4 shadow-social-card">
          <CardLabel className="mb-4">Emoji & formatting</CardLabel>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <SelectField
              label="Emoji usage"
              value={voice.emojiUsage}
              onChange={(v) => onChange({ emojiUsage: v })}
              options={EMOJI_OPTIONS}
            />
            <SelectField
              label="Post length preference"
              value={voice.postLengthPreference}
              onChange={(v) => onChange({ postLengthPreference: v })}
              options={LENGTH_OPTIONS}
            />
          </div>
          <div className="border-t border-border" />
          <ToggleRow
            label="Use line breaks for rhythm"
            sublabel="Short punchy lines vs. full paragraphs"
            checked={voice.useLineBreaks}
            onChange={(v) => onChange({ useLineBreaks: v })}
          />
          <div className="border-t border-border" />
          <ToggleRow
            label="Include questions to boost engagement"
            sublabel="AI may end posts with a question where natural"
            checked={voice.includeQuestions}
            onChange={(v) => onChange({ includeQuestions: v })}
          />
          <div className="border-t border-border" />
          <ToggleRow
            label="Reference brand color in image prompts"
            sublabel="Pass palette hex values to AI image generation"
            checked={voice.referenceBrandColor}
            onChange={(v) => onChange({ referenceBrandColor: v })}
          />
        </div>

        {/* Platform overrides */}
        <PlatformOverridesSection value={platformOverrides} onChange={onOverridesChange} />

        {/* AI context preview */}
        <AIContextPreview voice={voice} palette={palette} identity={identity} />
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Platform overrides
// ---------------------------------------------------------------------------

const PLATFORM_DISPLAY: Record<string, { icon: string; name: string }> = {
  instagram: { icon: '📸', name: 'Instagram' },
  tiktok: { icon: '🎵', name: 'TikTok' },
  twitter: { icon: '𝕏', name: 'X / Twitter' },
  linkedin: { icon: 'in', name: 'LinkedIn' },
};

interface PlatformOverride {
  platform: string;
  toneNote: string | null;
  isActive: boolean;
  anchorExamples: string[];
  antiExamples: string[];
}

const DEFAULT_OVERRIDES: PlatformOverride[] = [
  {
    platform: 'instagram',
    toneNote: 'Aesthetic-first, aspirational. Shorter captions, let the image lead.',
    isActive: true,
    anchorExamples: [],
    antiExamples: [],
  },
  {
    platform: 'tiktok',
    toneNote: 'More casual, trend-aware. Hook in first 2 words.',
    isActive: true,
    anchorExamples: [],
    antiExamples: [],
  },
  { platform: 'twitter', toneNote: null, isActive: false, anchorExamples: [], antiExamples: [] },
  {
    platform: 'linkedin',
    toneNote: 'Slightly more professional. Lead with insight, not product.',
    isActive: true,
    anchorExamples: [],
    antiExamples: [],
  },
];

function PlatformOverridesSection({
  value,
  onChange,
}: {
  value: PlatformOverride[];
  onChange: (overrides: PlatformOverride[]) => void;
}) {
  const [expanded, setExpanded] = useState<string | null>(null);

  function patch(platform: string, patch: Partial<PlatformOverride>) {
    onChange(value.map((o) => (o.platform === platform ? { ...o, ...patch } : o)));
  }

  function toggleOverride(platform: string) {
    const o = value.find((o) => o.platform === platform);
    if (o) patch(platform, { isActive: !o.isActive });
  }

  function setExample(
    platform: string,
    field: 'anchorExamples' | 'antiExamples',
    idx: number,
    text: string,
  ) {
    const o = value.find((o) => o.platform === platform);
    if (!o) return;
    const updated = [...o[field]];
    updated[idx] = text;
    patch(platform, { [field]: updated, isActive: true });
  }

  function addExample(platform: string, field: 'anchorExamples' | 'antiExamples') {
    const o = value.find((o) => o.platform === platform);
    if (!o) return;
    patch(platform, { [field]: [...o[field], ''], isActive: true });
  }

  function removeExample(platform: string, field: 'anchorExamples' | 'antiExamples', idx: number) {
    const o = value.find((o) => o.platform === platform);
    if (!o) return;
    patch(platform, { [field]: o[field].filter((_, i) => i !== idx) });
  }

  return (
    <div className="rounded-xl bg-background p-5 space-y-4 shadow-social-card">
      <div className="flex items-center gap-2 mb-4">
        <CardLabel className="mb-0">Platform-specific overrides</CardLabel>
        <OptionalBadge />
        <AiContextBadge />
      </div>
      <div className="space-y-0">
        {value.map((override, idx) => {
          const display = PLATFORM_DISPLAY[override.platform];
          if (!display) return null;
          const isExpanded = expanded === override.platform;
          const hasExamples =
            override.anchorExamples.length > 0 || override.antiExamples.length > 0;
          return (
            <div
              key={override.platform}
              className={cn(
                'transition-colors',
                idx < value.length - 1 ? 'border-b border-border' : '',
                !isExpanded && 'hover:bg-muted/50 hover:rounded-lg',
              )}
            >
              <div
                className="flex items-center gap-3.5 py-3 px-1 cursor-pointer group"
                onClick={() => setExpanded(isExpanded ? null : override.platform)}
              >
                <div
                  className={cn(
                    'h-8 w-8 rounded-[8px] border flex items-center justify-center text-[14px] shrink-0 transition-colors',
                    isExpanded
                      ? 'bg-foreground/5 border-foreground/20'
                      : 'bg-muted border-border group-hover:border-foreground/20',
                  )}
                >
                  {display.icon}
                </div>
                <span className="text-[13px] font-medium text-foreground min-w-[80px]">
                  {display.name}
                </span>
                <span className="flex-1 text-[12px] text-muted-foreground italic truncate">
                  {override.isActive && override.toneNote
                    ? `"${override.toneNote}"`
                    : '— same as global voice —'}
                </span>
                {hasExamples && (
                  <span className="hidden sm:inline text-[10px] text-muted-foreground shrink-0">
                    {override.anchorExamples.filter(Boolean).length +
                      override.antiExamples.filter(Boolean).length}{' '}
                    examples
                  </span>
                )}
                <ChevronDown
                  className={cn(
                    'h-3.5 w-3.5 text-muted-foreground transition-transform shrink-0',
                    isExpanded ? 'rotate-180' : 'group-hover:text-foreground',
                  )}
                />
                <span
                  className={cn(
                    'rounded-full text-[10px] font-medium px-2.5 py-0.5 shrink-0',
                    override.isActive
                      ? 'bg-[#E1F5EE] dark:bg-[#0d3d2d] text-[#085041] dark:text-[#5ecba1]'
                      : 'bg-muted text-muted-foreground',
                  )}
                >
                  {override.isActive ? 'Active' : 'Default'}
                </span>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleOverride(override.platform);
                  }}
                  className="text-[11px] text-muted-foreground hover:text-foreground ml-1"
                >
                  {override.isActive ? 'Reset' : 'Activate'}
                </button>
              </div>

              {isExpanded && (
                <div className="pb-4 space-y-4">
                  {/* Tone note */}
                  <div>
                    <p className="text-[11px] font-medium text-muted-foreground mb-1.5">
                      Tone note
                    </p>
                    <textarea
                      value={override.toneNote ?? ''}
                      onChange={(e) =>
                        patch(override.platform, {
                          toneNote: e.target.value || null,
                          isActive: true,
                        })
                      }
                      placeholder={`How should the AI write for ${display.name}?`}
                      rows={2}
                      className={cn(
                        'w-full rounded-[8px] px-[11px] py-2 border border-input bg-background text-sm text-foreground resize-none',
                        'focus:outline-none focus:ring-2 focus:ring-ring',
                        'resize-none rounded-xl border border-border bg-background px-3 py-2 text-[12px] text-foreground placeholder:text-muted-foreground/50 outline-none focus:border-primary leading-relaxed rounded-[10px] outline-none transition-all duration-150 shadow-social-card',
                      )}
                    />
                  </div>

                  {/* Anchor examples */}
                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <div>
                        <p className="text-[11px] font-medium text-foreground/80">
                          Anchor examples
                          <span className="ml-1.5 text-[10px] text-muted-foreground font-normal">
                            (best posts — AI uses these as style reference)
                          </span>
                        </p>
                      </div>
                      {override.anchorExamples.length < 5 && (
                        <button
                          type="button"
                          onClick={() => addExample(override.platform, 'anchorExamples')}
                          className="text-[11px] text-primary hover:underline"
                        >
                          + Add
                        </button>
                      )}
                    </div>
                    {override.anchorExamples.length === 0 ? (
                      <button
                        type="button"
                        onClick={() => addExample(override.platform, 'anchorExamples')}
                        className="w-full border border-dashed border-border rounded-[8px] py-2 text-[12px] text-muted-foreground hover:border-foreground/40 hover:text-foreground transition-colors"
                      >
                        + Add a best-performing post
                      </button>
                    ) : (
                      <div className="space-y-2">
                        {override.anchorExamples.map((ex, i) => (
                          <div key={i} className="flex gap-2 items-start">
                            <span className="text-[10px] text-muted-foreground pt-2.5 min-w-[14px]">
                              {i + 1}.
                            </span>
                            <textarea
                              value={ex}
                              onChange={(e) =>
                                setExample(override.platform, 'anchorExamples', i, e.target.value)
                              }
                              placeholder="Paste the post text exactly as published…"
                              rows={3}
                              className={cn(
                                'flex-1 rounded-[8px] px-[11px] py-2 border border-input bg-background text-sm text-foreground resize-none',
                                'focus:outline-none focus:ring-2 focus:ring-ring',
                                'resize-none rounded-xl border border-border bg-background px-3 py-2 text-[12px] text-foreground placeholder:text-muted-foreground/50 outline-none focus:border-primary leading-relaxed rounded-[10px] outline-none transition-all duration-150 shadow-social-card',
                              )}
                            />
                            <button
                              type="button"
                              onClick={() => removeExample(override.platform, 'anchorExamples', i)}
                              className="pt-2 text-muted-foreground hover:text-foreground"
                            >
                              <Plus className="h-3.5 w-3.5 rotate-45" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Anti-examples */}
                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <div>
                        <p className="text-[11px] font-medium text-foreground/80">
                          Anti-examples
                          <span className="ml-1.5 text-[10px] text-muted-foreground font-normal">
                            (posts that miss the mark — AI learns what to avoid)
                          </span>
                        </p>
                      </div>
                      {override.antiExamples.length < 2 && (
                        <button
                          type="button"
                          onClick={() => addExample(override.platform, 'antiExamples')}
                          className="text-[11px] text-primary hover:underline"
                        >
                          + Add
                        </button>
                      )}
                    </div>
                    {override.antiExamples.length === 0 ? (
                      <button
                        type="button"
                        onClick={() => addExample(override.platform, 'antiExamples')}
                        className="w-full border border-dashed border-border rounded-[8px] py-2 text-[12px] text-muted-foreground hover:border-foreground/40 hover:text-foreground transition-colors"
                      >
                        + Add a post that missed the mark
                      </button>
                    ) : (
                      <div className="space-y-2">
                        {override.antiExamples.map((ex, i) => (
                          <div key={i} className="flex gap-2 items-start">
                            <span className="text-[10px] text-muted-foreground pt-2.5 min-w-[14px]">
                              {i + 1}.
                            </span>
                            <textarea
                              value={ex}
                              onChange={(e) =>
                                setExample(override.platform, 'antiExamples', i, e.target.value)
                              }
                              placeholder="Paste the post text and optionally note why it missed…"
                              rows={3}
                              className={cn(
                                'flex-1 rounded-[8px] px-[11px] py-2 border border-input bg-muted/30 text-sm text-foreground resize-none',
                                'focus:outline-none focus:ring-2 focus:ring-ring',
                                'resize-none rounded-xl border border-border bg-background px-3 py-2 text-[12px] text-foreground placeholder:text-muted-foreground/50 outline-none focus:border-primary leading-relaxed rounded-[10px] outline-none transition-all duration-150 shadow-social-card',
                              )}
                            />
                            <button
                              type="button"
                              onClick={() => removeExample(override.platform, 'antiExamples', i)}
                              className="pt-2 text-muted-foreground hover:text-foreground"
                            >
                              <Plus className="h-3.5 w-3.5 rotate-45" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// AI Context Preview
// ---------------------------------------------------------------------------

const PERSONA_DESCRIPTIONS: Record<string, string> = {
  expert:
    'The Expert — Confident, knowledgeable, authoritative. Builds trust through depth and precision.',
  friend: 'The Friend — Warm, casual, relatable. Talks like a trusted peer, not a brand.',
  disruptor: 'The Disruptor — Bold, provocative, counter-narrative. Challenges the status quo.',
  storyteller:
    'The Storyteller — Narrative-driven, evocative, human. Leads with emotion and imagery.',
};

const SIGN_OFF_LABELS: Record<string, string> = {
  none: 'No sign-off',
  subtle_tagline: 'Subtle tagline reference',
  brand_mention: 'Brand name mention',
  call_to_action: 'Direct call to action',
  question: 'Closing question',
};

const CTA_LABELS: Record<string, string> = {
  none: 'No CTA',
  soft_engagement: 'Soft engagement (reply, save)',
  follow: 'Follow prompt',
  link_click: 'Link click',
  sale: 'Direct sale',
  signup: 'Sign-up / lead gen',
};

const EMOJI_LABELS: Record<string, string> = {
  none: 'No emoji',
  minimal: 'Minimal (1–2 per post)',
  moderate: 'Moderate (3–5 per post)',
  frequent: 'Frequent',
};

const LENGTH_LABELS: Record<string, string> = {
  short: 'Short (1-2 lines)',
  medium: 'Medium (3-5 lines)',
  long: 'Long (6+ lines)',
  varies: 'Varies by platform',
};

/** Builds the same Markdown context string that the backend injects into AI prompts. */
export function buildMarkdownPreview(
  identity: IdentityState,
  voice: VoiceState,
  palette: ColorSwatch[],
): string {
  const lines: string[] = [];

  lines.push('## Brand Context');
  if (identity.brandName) lines.push(`**Brand:** ${identity.brandName}`);
  if (identity.industry) lines.push(`**Industry:** ${identity.industry}`);
  if (identity.tagline) lines.push(`**Tagline:** ${identity.tagline}`);
  if (identity.website) lines.push(`**Website:** ${identity.website}`);

  lines.push('');
  lines.push('### Persona');
  lines.push(
    `**Archetype:** ${PERSONA_DESCRIPTIONS[voice.personaArchetype] ?? voice.personaArchetype}`,
  );

  lines.push('');
  lines.push('### Tone Spectrum');
  lines.push(`- **Formality:** ${voice.toneSpectrum.formality}/100 (Casual → Formal)`);
  lines.push(`- **Humor:** ${voice.toneSpectrum.humor}/100 (Serious → Playful)`);
  lines.push(`- **Boldness:** ${voice.toneSpectrum.boldness}/100 (Understated → Bold)`);
  lines.push(`- **Empathy:** ${voice.toneSpectrum.empathy}/100 (Direct → Empathetic)`);

  if (voice.voiceDescriptors.length > 0 || voice.avoidDescriptors.length > 0) {
    lines.push('');
    lines.push('### Voice Descriptors');
    if (voice.voiceDescriptors.length > 0)
      lines.push(`**Use:** ${voice.voiceDescriptors.join(', ')}`);
    if (voice.avoidDescriptors.length > 0)
      lines.push(`**Avoid:** ${voice.avoidDescriptors.join(', ')}`);
  }

  if (voice.brandDescription) {
    lines.push('');
    lines.push('### Brand Description');
    lines.push(voice.brandDescription);
  }

  if (voice.targetAudience) {
    lines.push('');
    lines.push('### Target Audience');
    lines.push(voice.targetAudience);
  }

  if (voice.contentPillars) {
    lines.push('');
    lines.push('### Content Pillars');
    const pillars = voice.contentPillars
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    pillars.forEach((p) => {
      console.log({ p, voice: voice.pillarWeights });
      const w = voice.pillarWeights[p];
      lines.push(w ? `- ${p} (${w}%)` : `- ${p}`);
    });
  }

  if (voice.postStructure) {
    lines.push('');
    lines.push('### Post Structure');
    lines.push(voice.postStructure);
  }

  if (voice.differentiationFraming) {
    lines.push('');
    lines.push('### Competitive Positioning');
    lines.push(voice.differentiationFraming);
  }

  if (voice.personaVoice) {
    lines.push('');
    lines.push('### Persona Voice');
    lines.push(voice.personaVoice);
  }

  const hasVocab = voice.wordsToUse || voice.wordsToNeverUse || voice.brandSpecificTerms;
  if (hasVocab) {
    lines.push('');
    lines.push('### Vocabulary');
    if (voice.wordsToUse) lines.push(`**Words to use:** ${voice.wordsToUse}`);
    if (voice.wordsToNeverUse) lines.push(`**Never use:** ${voice.wordsToNeverUse}`);
    if (voice.brandSpecificTerms) lines.push(`**Brand spellings:** ${voice.brandSpecificTerms}`);
  }

  if (voice.hashtagStrategy) {
    lines.push('');
    lines.push('### Hashtag Strategy');
    lines.push(voice.hashtagStrategy);
  }

  lines.push('');
  lines.push('### Formatting');
  lines.push(`- Emoji: ${EMOJI_LABELS[voice.emojiUsage] ? voice.emojiUsage : 'none'}`);
  lines.push(
    `- Post length: ${LENGTH_LABELS[voice.postLengthPreference] ?? voice.postLengthPreference}`,
  );
  lines.push(`- Use line breaks for rhythm: ${voice.useLineBreaks ? 'Yes' : 'No'}`);
  lines.push(`- Include engagement questions: ${voice.includeQuestions ? 'Yes' : 'No'}`);

  if (palette.length > 0) {
    lines.push('');
    lines.push('### Brand Colors');
    lines.push(palette.map((c) => `${c.hex} (${c.name})`).join(' · '));
    if (voice.referenceBrandColor) {
      lines.push('*Reference these colors in image generation prompts.*');
    }
  }

  lines.push('');
  lines.push('### Sign-off & CTA');
  lines.push(`- Sign-off style: ${SIGN_OFF_LABELS[voice.signOffStyle] ?? voice.signOffStyle}`);
  lines.push(`- Default CTA: ${CTA_LABELS[voice.defaultCTAType] ?? voice.defaultCTAType}`);

  return lines.join('\n');
}

function AIContextPreview({
  voice,
  palette,
  identity,
}: {
  voice: VoiceState;
  palette: ColorSwatch[];
  identity: IdentityState;
}) {
  const [copied, setCopied] = useState(false);

  const markdownStr = buildMarkdownPreview(identity, voice, palette);

  function handleCopy() {
    navigator.clipboard.writeText(markdownStr).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  // Syntax highlight the Markdown for display in the dark code block
  function highlightMarkdown(md: string) {
    return md
      .split('\n')
      .map((line) => {
        // ## headings — amber
        if (/^#{1,3} /.test(line)) {
          return `<span style="color:#FAC775">${line}</span>`;
        }
        // **label:** value — label in purple, value in light
        if (/^\*\*[^*]+:\*\*/.test(line)) {
          return line.replace(/(\*\*[^*]+:\*\*)(.*)/, (_, label, rest) => {
            return `<span style="color:#7F77DD">${label}</span><span style="color:#C9C7C1">${rest}</span>`;
          });
        }
        // - list item — teal bullet, light text
        if (/^- /.test(line)) {
          return `<span style="color:#9FE1CB">-</span><span style="color:#C9C7C1">${line.slice(1)}</span>`;
        }
        // *italic* — muted
        if (/^\*[^*]/.test(line)) {
          return `<span style="color:#5F5E5A">${line}</span>`;
        }
        // --- separator
        if (/^---$/.test(line.trim())) {
          return `<span style="color:#3A3835">${line}</span>`;
        }
        return `<span style="color:#C9C7C1">${line}</span>`;
      })
      .join('\n');
  }

  return (
    <div className="rounded-xl bg-background p-5 space-y-4 shadow-social-card">
      <div className="flex items-center justify-between mb-3">
        <div>
          <p className="text-[12px] font-medium text-foreground">AI context preview</p>
          <p className="text-[11px] text-muted-foreground mt-0.5">
            This is what gets injected into the composer's system prompt
          </p>
        </div>
        <Button variant="outline" size="sm" className="h-7 text-xs" onClick={handleCopy}>
          <Copy className="h-3 w-3 mr-1" />
          {copied ? 'Copied!' : 'Copy context'}
        </Button>
      </div>
      <div
        className="rounded-[9px] px-[18px] py-4 text-[12px] overflow-x-auto"
        style={{ background: 'var(--social-code-bg)', fontFamily: 'monospace', lineHeight: '1.75' }}
      >
        <div style={{ color: '#5F5E5A', marginBottom: '8px' }}>
          {'// brand_context.md — injected into AI system prompt'}
        </div>
        <div
          dangerouslySetInnerHTML={{ __html: highlightMarkdown(markdownStr) }}
          style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

function toStringOrEmpty(v: string | null | undefined): string {
  return v ?? '';
}

export function BrandSettingsPage() {
  const { selectedHolderId } = useHolderSwitcher();
  const { settings, isLoading, isSaving, error, saveSettings } = useBrandSettings(selectedHolderId);

  const [identity, setIdentity] = useState<IdentityState>({
    brandName: '',
    industry: '',
    tagline: '',
    website: '',
  });

  const [logo, setLogo] = useState<LogoState>({
    lightLogoFileId: null,
    darkLogoFileId: null,
    usage: 'always',
    placement: 'bottom_right',
  });

  const [palette, setPalette] = useState<ColorSwatch[]>([]);
  const [voice, setVoice] = useState<VoiceState>({
    personaArchetype: 'expert',
    toneSpectrum: { formality: 30, humor: 55, boldness: 70, empathy: 60 },
    voiceDescriptors: [],
    avoidDescriptors: [],
    brandDescription: '',
    targetAudience: '',
    contentPillars: '',
    pillarWeights: {},
    postStructure: '',
    differentiationFraming: '',
    personaVoice: '',
    signOffStyle: 'subtle_tagline',
    defaultCTAType: 'soft_engagement',
    wordsToUse: '',
    wordsToNeverUse: '',
    brandSpecificTerms: '',
    hashtagStrategy: '',
    emojiUsage: 'minimal',
    postLengthPreference: 'medium',
    useLineBreaks: true,
    includeQuestions: true,
    referenceBrandColor: true,
  });

  const [platformOverrides, setPlatformOverrides] = useState<PlatformOverride[]>(DEFAULT_OVERRIDES);
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (settings && !initialized) {
      setIdentity({
        brandName: toStringOrEmpty(settings.identity.brandName),
        industry: toStringOrEmpty(settings.identity.industry),
        tagline: toStringOrEmpty(settings.identity.tagline),
        website: toStringOrEmpty(settings.identity.website),
      });
      setLogo({
        lightLogoFileId: settings.logo.lightLogoFileId,
        darkLogoFileId: settings.logo.darkLogoFileId,
        usage: settings.logo.usage,
        placement: settings.logo.placement,
      });
      setPalette(settings.palette ?? []);
      setVoice({
        personaArchetype: settings.voice.personaArchetype ?? 'expert',
        toneSpectrum: settings.voice.toneSpectrum ?? {
          formality: 30,
          humor: 55,
          boldness: 70,
          empathy: 60,
        },
        voiceDescriptors: settings.voice.voiceDescriptors ?? [],
        avoidDescriptors: settings.voice.avoidDescriptors ?? [],
        brandDescription: toStringOrEmpty(settings.voice.brandDescription),
        targetAudience: toStringOrEmpty(settings.voice.targetAudience),
        contentPillars: toStringOrEmpty(settings.voice.contentPillars),
        pillarWeights:
          (settings.voice as { pillarWeights?: Record<string, number> }).pillarWeights ?? {},
        postStructure: toStringOrEmpty(
          (settings.voice as { postStructure?: string | null }).postStructure,
        ),
        differentiationFraming: toStringOrEmpty(
          (settings.voice as { differentiationFraming?: string | null }).differentiationFraming,
        ),
        personaVoice: toStringOrEmpty(settings.voice.personaVoice),
        signOffStyle: settings.voice.signOffStyle ?? 'subtle_tagline',
        defaultCTAType: settings.voice.defaultCTAType ?? 'soft_engagement',
        wordsToUse: toStringOrEmpty(settings.voice.wordsToUse),
        wordsToNeverUse: toStringOrEmpty(settings.voice.wordsToNeverUse),
        brandSpecificTerms: toStringOrEmpty(settings.voice.brandSpecificTerms),
        hashtagStrategy: toStringOrEmpty(settings.voice.hashtagStrategy),
        emojiUsage: settings.voice.emojiUsage ?? 'minimal',
        postLengthPreference: settings.voice.postLengthPreference ?? 'medium',
        useLineBreaks: settings.voice.useLineBreaks ?? true,
        includeQuestions: settings.voice.includeQuestions ?? true,
        referenceBrandColor: settings.voice.referenceBrandColor ?? true,
      });
      if (settings.platformOverrides?.length) {
        setPlatformOverrides(
          settings.platformOverrides.map((o) => ({
            platform: o.platform,
            toneNote: o.toneNote,
            isActive: o.isActive,
            anchorExamples: (o as { anchorExamples?: string[] }).anchorExamples ?? [],
            antiExamples: (o as { antiExamples?: string[] }).antiExamples ?? [],
          })),
        );
      }
      setInitialized(true);
    }
  }, [settings, initialized]);

  const initials = identity.brandName
    .split(' ')
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');

  const handleSave = useCallback(async () => {
    if (!identity.brandName.trim()) {
      toast({ title: 'Brand name is required', variant: 'destructive' });
      return;
    }
    try {
      await saveSettings({
        identity: {
          brandName: identity.brandName,
          industry: identity.industry || null,
          tagline: identity.tagline || null,
          website: identity.website || null,
        },
        logo: {
          lightLogoFileId: logo.lightLogoFileId,
          darkLogoFileId: logo.darkLogoFileId,
          usage: logo.usage,
          placement: logo.placement,
        },
        palette,
        voice: {
          personaArchetype: voice.personaArchetype,
          toneSpectrum: voice.toneSpectrum,
          voiceDescriptors: voice.voiceDescriptors,
          avoidDescriptors: voice.avoidDescriptors,
          brandDescription: voice.brandDescription || null,
          targetAudience: voice.targetAudience || null,
          contentPillars: voice.contentPillars || null,
          pillarWeights: Object.keys(voice.pillarWeights).length ? voice.pillarWeights : null,
          postStructure: voice.postStructure || null,
          differentiationFraming: voice.differentiationFraming || null,
          personaVoice: voice.personaVoice || null,
          signOffStyle: voice.signOffStyle,
          defaultCTAType: voice.defaultCTAType,
          wordsToUse: voice.wordsToUse || null,
          wordsToNeverUse: voice.wordsToNeverUse || null,
          brandSpecificTerms: voice.brandSpecificTerms || null,
          hashtagStrategy: voice.hashtagStrategy || null,
          emojiUsage: voice.emojiUsage,
          postLengthPreference: voice.postLengthPreference,
          useLineBreaks: voice.useLineBreaks,
          includeQuestions: voice.includeQuestions,
          referenceBrandColor: voice.referenceBrandColor,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } as any,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        platformOverrides: platformOverrides as any,
      });
      toast({ title: 'Brand settings saved' });
    } catch {
      toast({ title: 'Failed to save', description: 'Please try again.', variant: 'destructive' });
    }
  }, [identity, logo, palette, voice, platformOverrides, saveSettings]);

  if (isLoading) {
    return (
      <div className="px-4 sm:pl-9 pt-6 sm:pt-8 max-w-[820px] space-y-7 pb-16">
        <Skeleton className="h-8 w-40" />
        <Skeleton className="h-40 w-full rounded-xl" />
        <Skeleton className="h-40 w-full rounded-xl" />
        <Skeleton className="h-64 w-full rounded-xl" />
      </div>
    );
  }

  return (
    <div className="w-full h-auto bg-social-bg">
      <div className="px-4 sm:pl-9 pt-6 sm:pt-8 max-w-[820px] pb-16 bg-social-bg">
        {/* Page header */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-6 sm:mb-8">
          <div>
            <h1 className="font-satoshi text-2xl font-normal text-foreground">Brand Settings</h1>
            <p className="text-[13px] text-muted-foreground mt-1 font-satoshi">
              Configure your brand identity and AI voice for content generation.
            </p>
          </div>
          <Button
            onClick={handleSave}
            disabled={isSaving}
            className="self-start shrink-0 !font-mono !py-1 !px-2 text-[10px] h-auto"
          >
            <FloppyIcon size={13} />
            {isSaving ? 'Saving…' : 'Save changes'}
          </Button>
        </div>

        {error && (
          <div className="mb-6 p-3 rounded-md bg-destructive/10 text-destructive text-sm">
            {error}
          </div>
        )}

        <div className="space-y-7">
          <IdentitySection
            identity={identity}
            onChange={(patch) => setIdentity((p) => ({ ...p, ...patch }))}
          />
          <LogoSection
            logo={logo}
            initials={initials}
            onChange={(patch) => setLogo((p) => ({ ...p, ...patch }))}
          />
          <ColorPaletteSection palette={palette} onChange={setPalette} />
          <BrandVoiceSection
            voice={voice}
            palette={palette}
            identity={identity}
            platformOverrides={platformOverrides}
            onChange={(patch) => setVoice((p) => ({ ...p, ...patch }))}
            onOverridesChange={setPlatformOverrides}
          />
        </div>
      </div>
    </div>
  );
}
